import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import {
  arrayToObj,
  echartsContent,
  getPercentageInfo,
  transLanguage,
} from '@op/utils';
import { SymbolType } from '@op/constants/chart';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { getXAxisAxisLabel } from '@op/utils/chart';
import { useIntl } from 'umi';
import { NO_DATE } from '@op/constants';
import {
  ByKeyMetricsType,
  CommunityOptionsType,
  getChartsOptions,
  getCurrentColumns,
  getEngagementTipSortLabel,
  KeyMetricsType,
} from '../constants';
import {
  channelInfo,
  channelLegendRichInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import { reviewsFormat } from '@op/utils/value';
import { formatTimeToShow } from '@op/utils/time';
import { dealWithOthersName } from '@op/utils/languageValue';
import { measureTextWidth } from '../../MentionsSentiment/Channel';
import { DBO_EC } from '@op/utils/format';
import {
  detailPageTips,
  engagementTypeDetailPageTips,
  topReviewsTips,
} from '../../utils';
import TopPanel from './TopPanel';
import { isTopChartType, isTopMetric } from './constants/topPanel';
import { useCommunityTopPanel } from './hooks/useCommunityTopPanel';

const heightMap: any = {
  'common-bar': 366,
};

export default function Index({
  tab,
  keyMetric,
  filter,
  chartType,
  byType,
  data,
  title,
  isPercent = false,
  formatValue = reviewsFormat,
  eventsDict,
  onChartReady,
  detailPage = false,
  isDetail = false,
  gameInfo,
  eventProps,
  formatParamsKey,
  pageFilter,
}: {
  tab: CommunityOptionsType;
  keyMetric: KeyMetricsType;
  byType: ByKeyMetricsType;
  filter: any;
  chartType: SymbolType;
  data: any;
  title: string | undefined;
  formatValue?: (value: number) => string | number;
  isPercent?: boolean;
  eventsDict?: any;
  onChartReady?: (chart: any) => void;
  detailPage?: boolean;
  isDetail?: boolean;
  gameInfo?: any;
  eventProps: {
    key: string;
    defaultFilter: Record<string, any>;
    defaultProps: Record<string, any>;
    paramsKey: { [chartType: string]: any } | string;
    defaultConfig?: Record<string, any>;
  };
  formatParamsKey?: (
    eventKeyValue: string | number | any,
    key: string,
  ) => { [key: string]: any } | string;
  pageFilter: Record<string, any>;
}) {
  const { formatMessage } = useIntl();
  const enableTopPopover = useMemo(() => {
    return (
      !isDetail &&
      tab !== 'streaming' &&
      byType !== 'by_follower_range' &&
      isTopMetric(keyMetric) &&
      isTopChartType(chartType)
    );
  }, [isDetail, tab, byType, keyMetric, chartType]);

  const {
    visible: topPanelVisible,
    list: topPanelList,
    loading: topPanelLoading,
    clickParams: topPanelClickParams,
    position: topPanelPosition,
    handleSeriesClick,
    closePanel,
    bindChart,
    handleMore,
  } = useCommunityTopPanel({
    enabled: enableTopPopover,
    filter,
    tab,
    metric: keyMetric,
    chartType,
    eventProps,
    formatParamsKey,
    resetDeps: [pageFilter, tab, keyMetric, chartType, byType],
  });

  const activeDetailPage = enableTopPopover ? false : detailPage;
  const showTopMode = enableTopPopover;
  const dateType = pageFilter?.date_type || 'daily';

  const handleChartReady = useCallback(
    (chart: any) => {
      bindChart(chart);
    },
    [bindChart],
  );

  const chartRealType = useMemo(() => {
    if (chartType === 'stack_line') {
      return 'line';
    }
    if (chartType === 'common-bar') {
      return 'bar';
    }
    return chartType;
  }, [chartType]);
  const hideTitle = chartType === 'pie' || chartType === 'common-bar';
  const eChartsRef = useRef<any>(null);
  const hoveredSeriesNameRef = useRef<string | undefined>();
  const chartWrapRef = useRef<HTMLDivElement>(null);

  // Close top panel when clicking outside the chart & panel area
  useEffect(() => {
    if (!enableTopPopover || !topPanelVisible) return;

    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // TopPanel already calls stopPropagation, so we only need to
      // ignore clicks that land inside the chart wrap div (handled by ECharts)
      if (chartWrapRef.current?.contains(target)) return;
      // TranslateSelect dropdown is rendered via portal outside chartWrapRef
      if ((target as HTMLElement).closest?.('.select-translate-box-wrapper')) return;
      closePanel();
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [enableTopPopover, topPanelVisible, closePanel]);

  const getHeaderByType = (
    pieData: {
      name: string;
      value: number | string;
      itemStyle: { color: string };
    }[],
  ) => {
    const chartConfig = getCurrentColumns({
      tab,
      keyMetric,
      byTypeKeyMetric: byType,
    });

    if (typeof chartConfig === 'object') {
      const obj = arrayToObj(chartConfig, 'key');
      return (
        pieData?.map((val) => ({
          ...val,
          name: obj?.[val.name]?.header || val.name,
        })) || []
      );
    }
    return pieData;
  };

  const getPieChartOptions = ({
    data: paramsData,
    filter,
    byType,
    chartType,
  }: any) => {
    const { pieData, pieTotalData } = paramsData || {};
    const data = getHeaderByType(pieData);
    const type = byType !== 'total' ? byType.split('_')[1] : '';

    const options = {
      series: {
        data:
          data?.map((val) => ({
            name: val.name,
            value: val.value,
            ...(chartType === 'pie' ? { itemStyle: val.itemStyle } : {}),
          })) || [],
        type: chartType,
        label: {
          color: '#262832',
          alignTo: 'edge',
          formatter: (params: any) => {
            const percent = getPercentageInfo(params?.value, pieTotalData, 2);
            let name = formatMessage({ id: params.name });
            if (byType === 'total') {
              name = formatMessage({ id: title || NO_DATE.EMPTY });
            } else if (
              type === 'channel' ||
              type === 'language' ||
              type === 'region'
            ) {
              name = dealWithOthersName(params.name, type);
            }
            let icon = '';
            if (type === 'channel') {
              icon = `{${dealwithChannelChartKey(params.name)}|}`;
            }

            let percentText = formatValue(params.value);
            if (params?.value) {
              percentText += `(${percent})`;
            }

            return `${icon}{value| ${name}}: {a|${percentText}}\n{hrLine|}\n{a|}`;
          },
          edgeDistance: 10,
          rich: {
            a: {
              // color: '#999',
              lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
              align: 'center',
            },
            hrLine: {
              // 设置hr是为了让中间线能够自适应长度
              borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
              width: '105%',
              borderWidth: 0.5,
              height: 0.5,
            },
            per: {
              // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
              padding: [4, 0],
            },
            ...channelLegendRichInfo(16, 16),
          },
        },
        labelLayout: (params: any) => {
          const isLeft =
            params.labelRect.x <
            eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
          const points = params.labelLinePoints;
          // Update the end point.
          points[2][0] = isLeft
            ? params.labelRect.x
            : params.labelRect.x + params.labelRect.width;
          return {
            labelLinePoints: points,
          };
        },
        emphasis: {
          focus: 'self',
          label: {
            opacity: 0.2,
          },
          labelLine: {
            lineStyle: {
              opacity: 0.2,
            },
          },
        },
        blur: {
          label: { opacity: 0.2 },
          itemStyle: {
            opacity: 0.2,
          },
          labelLine: {
            lineStyle: {
              opacity: 0.2,
            },
          },
        },
      },
      legend: {
        data,
        formatter: (params: any) => {
          if (byType === 'total') {
            return formatMessage({ id: title || NO_DATE.EMPTY });
          }
          if (byType === 'by_channel' && params !== 'Others') {
            const { name } = channelInfo(params);
            return `{${dealwithChannelChartKey(params)}|} {name| ${name}}`;
          }
          if (byType === 'by_language' && params !== 'Others') {
            return transLanguage(params);
          }
          return formatMessage({ id: params });
        },
      },
      tooltip: {
        borderWidth: 0,
        borderRadius: 6,
        padding: 0,
        formatter: (params: any) => {
          const sortByParams = Array.isArray(params) ? params : [params];
          const sortBy = getEngagementTipSortLabel(sortByParams[0]?.name);
          return formatChartParamsDate({
            params,
            listTitleFunc: (item: any) => {
              const isNoOther = item.name !== 'Others';
              if (byType === 'total') {
                return formatMessage({ id: title || NO_DATE.EMPTY });
              }
              if (byType === 'by_channel') {
                const { img, name } = channelInfo(item.name);
                return `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${isNoOther ? name : formatMessage({ id: item.name })}<span>`;
              }
              if (byType === 'by_language' && isNoOther) {
                return transLanguage(item.name);
              }
              return formatMessage({ id: item.name || NO_DATE.EMPTY });
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: formatTimeToShow(filter),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: title || NO_DATE.EMPTY }),
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
            valueFormat: (value) => {
              const percent = getPercentageInfo(value, pieTotalData, 2);
              return `${isNaN(value) ? '-' : formatValue(value)}(${percent})`;
            },
            dealwithRecords: (records) => {
              if (byType === 'by_engagement_type') {
                engagementTypeDetailPageTips(
                  records,
                  formatMessage,
                  activeDetailPage,
                  sortBy,
                );
              } else {
                detailPageTips(records, formatMessage, activeDetailPage);
              }
              return records;
            },
          });
        },
      },
      title: {
        text: `{name|${formatValue(pieTotalData)}}`,
        top: '43%',
        left: 'center',
        textStyle: {
          rich: {
            name: {
              fontSize: 24,
              fontFamily: 'num-m',
              color: '#000000',
              padding: [10, 0],
            },
          },
        },
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '34%',
          style: {
            fill: '#444444',
            fontSize: 12,
          },
        },
      ],
    };

    if (chartType !== 'pie') {
      const { title: chartTitle, graphic, series, ...restOptions } = options;
      const dataY = data?.map((item: any) => item.name) || [];
      const maxLabelWidth = type
        ? Math.max(...(measureTextWidth(dataY, type) || [])) || 60
        : 60;
      const rounded = Math.round(maxLabelWidth / 10) * 10;
      const width = (rounded > maxLabelWidth ? rounded : rounded + 10) + 15;

      const rich = dataY.reduce((acc: any, item: any) => {
        const { img } = channelInfo(item);
        acc[dealwithChannelChartKey(item)] = {
          width: 16,
          height: 16,
          backgroundColor: { image: img },
        };
        return acc;
      }, {});

      return {
        ...restOptions,
        series: {
          ...series,
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => formatValue(params.value),
            fontSize: 12,
          },
          labelLayout: (params) => params,
          barMaxWidth: 80,
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
            fontSize: 12,
          },
          axisLine: {
            show: true,
            lineStyle: { color: '#E5E5E5', type: 'solid' },
          },
          splitLine: {
            show: true,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
        },
        yAxis: [
          {
            type: 'category',
            position: 'left',
            axisLabel: {
              interval: 0,
              show: byType === 'by_channel',
              formatter: (value: any) => `{${dealwithChannelChartKey(value)}|}`,
              rich,
              width: 20,
              align: 'right',
              padding: [0, width + 5, 0, 0],
            },
            splitLine: {
              show: false,
              lineStyle: { color: '#E5E5E5', type: 'dashed' },
            },
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            data: dataY,
          },
          {
            type: 'category',
            position: 'left',
            axisLabel: {
              interval: 0,
              show: true,
              align: 'right',
              formatter: (value: any) => {
                if (
                  type === 'channel' ||
                  type === 'language' ||
                  type === 'region'
                ) {
                  return `{label|${dealWithOthersName(value, type)}}`;
                }
                if (byType === 'total') {
                  return `{label|${formatMessage({ id: title || NO_DATE.EMPTY })}}`;
                }
                return `{label|${formatMessage({ id: value || NO_DATE.EMPTY })}}`;
              },
              rich: {
                label: {
                  align: 'left',
                },
              },
              padding: [0, 0, 0, 0],
              overflow: 'break',
              fontWeight: 400,
              fontSize: 12,
              lineHeight: 16,
              width,
            },
            splitLine: {
              show: false,
              lineStyle: { color: '#E5E5E5', type: 'dashed' },
            },
            axisLine: {
              show: true,
              lineStyle: { color: '#E5E5E5', type: 'solid' },
            },
            axisTick: {
              show: false,
            },
            data: dataY,
          },
        ],
        grid: {
          right: '5%',
          bottom: 5,
          contentLabel: true,
        },
      };
    }

    return options;
  };

  const options = useMemo(() => {
    if (!byType) return {};

    if (chartType === 'pie' || chartType === 'common-bar') {
      return getPieChartOptions({
        data,
        filter,
        byType,
        chartType: chartRealType,
      });
    }

    const dateType = filter.date_type;
    const { series, legend } = getChartsOptions({
      data: data?.yAxisData || {},
      chartType,
      chartRealType: chartRealType as any,
      keys: data?.tableColumns,
    });

    return {
      series,
      legend: {
        ...legend,
        formatter: (params: any) => {
          if (byType === 'total') {
            return formatMessage({ id: title || NO_DATE.EMPTY });
          }
          if (byType === 'by_channel' && params !== 'Others') {
            const { name } = channelInfo(params);
            return `{${dealwithChannelChartKey(params)}|} {name| ${name}}`;
          }
          if (byType === 'by_language' && params !== 'Others') {
            return transLanguage(params);
          }
          return formatMessage({ id: params });
        },
      },
      tooltip: {
        show: !topPanelVisible,
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        formatter: (params: any) => {
          const chartParams = Array.isArray(params) ? params : [params];
          const sortBy = getEngagementTipSortLabel(
            hoveredSeriesNameRef.current || chartParams[0]?.seriesName,
          );

          const leftTitle = listTimeTitleFunc(params[0]?.axisValue, dateType);
          const rightTitle = formatMessage({
            id: isPercent ? title : NO_DATE.EMPTY,
          });
          return formatChartParamsDate({
            title: leftTitle,
            rightTitle,
            params,
            isSort: true,
            listTitleFunc: (item: any) => {
              const isNoOther = item.seriesName !== 'Others';
              if (byType === 'total') {
                return formatMessage({ id: title || NO_DATE.EMPTY });
              }
              if (byType === 'by_channel') {
                const { img, name } = channelInfo(item.seriesName);
                return `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${isNoOther ? name : formatMessage({ id: 'Others' })}<span>`;
              }
              if (byType === 'by_language' && isNoOther) {
                return transLanguage(item.seriesName);
              }
              return formatMessage({ id: item.seriesName || NO_DATE.EMPTY });
            },
            valueFormat(value) {
              return formatValue(value);
            },
            dealwithRecords: (records, total) => {
              const list = records.map((item) => {
                if (item.originValue === 0) return item;

                if (isPercent || byType === 'total') return item;

                return {
                  ...item,
                  value: `${item.value}(${getPercentageInfo(
                    item.originValue,
                    total,
                    2,
                  )})`,
                };
              });

              if (!isPercent && byType !== 'total') {
                list.unshift({
                  text: formatMessage({ id: title || NO_DATE.EMPTY }),
                  value: formatValue(total) as any,
                  originValue: total,
                  originText: title as any,
                });
              }

              topReviewsTips(
                list,
                formatMessage,
                showTopMode && !topPanelVisible,
              );
              if (byType === 'by_engagement_type') {
                engagementTypeDetailPageTips(
                  list,
                  formatMessage,
                  activeDetailPage && !(showTopMode && !topPanelVisible),
                  sortBy,
                );
              } else {
                detailPageTips(
                  list,
                  formatMessage,
                  activeDetailPage && !(showTopMode && !topPanelVisible),
                );
              }

              return list;
            },
          });
        },
      },
      xAxis: {
        axisLabel: getXAxisAxisLabel(filter),
        data: data?.xAxisData || [],
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return DBO_EC.reviewsPercent(value, isPercent, isPercent);
            },
            color: '#838697',
          },
          nameTextStyle: {
            color: '#444444',
            fontWeight: 400,
            fontSize: '12px',
          },
          splitNumber: 5,
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
          },
        },
      ],
      grid: {
        left: 5,
        right: 5,
      },
    };
  }, [filter, data, tab, byType, chartType, chartRealType, formatMessage, activeDetailPage, showTopMode, topPanelVisible]);

  const mergedEvents = useMemo(() => {
    if (!eventsDict || typeof eventsDict === 'function') {
      return eventsDict;
    }

    return {
      ...eventsDict,
      click: (event: any) => {
        if (enableTopPopover) {
          if (event?.componentType === 'series') {
            handleSeriesClick(event);
            return;
          }
          closePanel();
          return;
        }
        eventsDict.click?.(event);
      },
      legendselectchanged: (params: any) => {
        if (enableTopPopover) {
          closePanel();
        }
        eventsDict.legendselectchanged?.(params);
      },
      mouseover: (event: any) => {
        eventsDict.mouseover?.(event);

        if (
          byType === 'by_engagement_type' &&
          event.componentType === 'series'
        ) {
          hoveredSeriesNameRef.current = event.seriesName;
        }
      },
    };
  }, [eventsDict, byType, enableTopPopover, handleSeriesClick, closePanel]);

  return (
    <div className="dbo-community-left-chart__chart-wrap" ref={chartWrapRef}>
      <EChartCommon
        optionsProps={{ type: chartRealType }}
        height={heightMap[chartType] || 338}
        leftTitle={
          hideTitle ? undefined : formatMessage({ id: title || NO_DATE.EMPTY })
        }
        options={options}
        ref={eChartsRef}
        eventsDict={mergedEvents}
        onChartReady={!enableTopPopover ? onChartReady : handleChartReady}
      />
      {enableTopPopover ? (
        <TopPanel
          visible={Boolean(topPanelVisible)}
          loading={Boolean(topPanelLoading)}
          list={topPanelList}
          metric={keyMetric}
          clickParams={topPanelClickParams}
          dateType={dateType}
          position={topPanelPosition}
          onMore={handleMore}
          gameInfo={gameInfo}
        />
      ) : null}
    </div>
  );
}
